import { defineStore } from 'pinia';
import axios from '@services/axios';
import { useSessionStore } from '@stores/session';
import AutoService from '@services/AutoService';
import { notify } from '@components/NotificationPlugin';

export type AssetFileType = 'all' | 'audio' | 'video' | 'image' | 'srt' | 'transcript' | 'other';
export type AssetSortBy = 'newest' | 'oldest' | 'name' | 'size' | 'duration';

export interface AssetItem {
  id: string;
  PK: string;
  SK: string;
  elementId: string;
  fileName: string;
  name?: string;
  fileUrl?: string;
  size?: number;
  duration?: number;
  contentType?: string;
  filetype?: string;
  type: string;
  language?: string;
  preview?: string;
  createdAt: string;
  updatedAt?: string;
  projectId?: string;
  projectName?: string;
  workspaceId?: string;
  workspaceName?: string;
  inputId?: string;
  outputId?: string;
  inputName?: string;
  userId?: string;
  status?: string;
  // derived
  displayType: AssetFileType;
}

export interface AssetsStats {
  totalFiles: number;
  totalSize: number;
  byType: Record<string, number>;
  byTypeSize: Record<string, number>;
}

export interface ParentTrailSegment {
  icon: string;
  /** Short label shown in the breadcrumb (real name, else truncated id). */
  label: string;
  /** Full identification for the title tooltip. */
  full: string;
}

const shortId = (id: string): string =>
  id.length > 12 ? `${id.slice(0, 8)}…` : id;

/** Breadcrumb segments identifying an asset's parents (workspace › project).
 *  Names preferred; falls back to truncated ids; drops segments with neither
 *  so the UI never renders fabricated placeholders. */
export function assetParentTrail(item: Pick<AssetItem, 'workspaceId' | 'workspaceName' | 'projectId' | 'projectName'>): ParentTrailSegment[] {
  const trail: ParentTrailSegment[] = [];
  if (item.workspaceName || item.workspaceId) {
    const name = item.workspaceName || '';
    const id = item.workspaceId || '';
    trail.push({
      icon: 'fas fa-layer-group',
      label: name || shortId(id),
      full: name ? (id ? `${name} (${id})` : name) : id,
    });
  }
  if (item.projectName || item.projectId) {
    const name = item.projectName || '';
    const id = item.projectId || '';
    trail.push({
      icon: 'fas fa-folder',
      label: name || shortId(id),
      full: name ? (id ? `${name} (${id})` : name) : id,
    });
  }
  return trail;
}

interface AssetsState {
  items: AssetItem[];
  filteredItems: AssetItem[];
  loading: boolean;
  searching: boolean;
  error: string | null;
  searchQuery: string;
  fileTypeFilter: AssetFileType;
  sortBy: AssetSortBy;
  viewMode: 'grid' | 'list';
  nextToken: string | null;
  hasMore: boolean;
  stats: AssetsStats;
  previewItem: AssetItem | null;
  showPreview: boolean;
  showExpired: boolean;
  projectNames: Record<string, string>;
  workspaceNames: Record<string, string>;
  inputNames: Record<string, string>;
  outputInputs: Record<string, string>;
  inputNameReady: Record<string, boolean>;
  // File currently being opened in the transcript dashboard (set by the
  // assets page before navigation, cleared by the dashboard once the
  // editor lands). Drives the cross-page "opening" loading overlay.
  openingFileName: string | null;
}

// Classification precedence: the file-size tracker writes magic-byte truth
// into `filetype` (and refreshes it) but never heals a stale row `type`, so
// the tracker verdict — including the backend's `fileType` alias — wins over
// the row type. Extension still decides when no verdict exists.
export function detectDisplayType(item: any): AssetFileType {
  const ct = String(item.contentType || item.filetype || item.fileType || item.type || '').toLowerCase();
  const name = String(item.fileName || item.name || '').toLowerCase();
  if (ct.includes('audio') || /\.(mp3|wav|m4a|ogg|flac|aac|wma)$/.test(name)) return 'audio';
  if (ct.includes('video') || /\.(mp4|mov|avi|mkv|webm|m4v)$/.test(name)) return 'video';
  if (ct.includes('image') || /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/.test(name)) return 'image';
  if (name.endsWith('.srt') || ct.includes('srt') || item.type === 'transcript') return 'srt';
  if (item.type === 'transcript' || item.type === 'translation' || item.type === 'prediction') return 'transcript';
  return 'other';
}

function normalizeAsset(raw: any): AssetItem {
  const fileName = raw.fileName || raw.name || raw.SK?.split('#')[1] || 'Untitled';
  const elementId = raw.elementId || raw.SK?.split('#')[1] || raw.PK || fileName;
  const pk = raw.PK || '';
  const sk = raw.SK || '';
  // try to extract workspace/project from PK/SK or explicit fields.
  // Element rows carry projectId but no workspaceId — never copy the
  // project id into workspaceId (that sent ensureNames to a bogus
  // workspace and names never resolved). Missing owners are resolved by
  // ensureNames() via a project scan instead.
  let workspaceId = raw.workspaceId || '';
  let projectId = raw.projectId || '';
  // PK patterns: WORKSPACE#<ws> or PROJECT#<proj> or INPUT#<id> etc.
  // For global assets, backend may return GSI with workspace/project info
  if (!workspaceId && raw.GSI1PK) {
    const m = String(raw.GSI1PK).match(/WORKSPACE#([^#]+)/);
    if (m) workspaceId = m[1];
  }
  if (!projectId && raw.GSI1SK) {
    const m = String(raw.GSI1SK).match(/PROJECT#([^#]+)/);
    if (m) projectId = m[1];
  }
  // fallback: try PK
  if (!workspaceId && pk.includes('WORKSPACE#')) {
    workspaceId = pk.split('WORKSPACE#')[1]?.split('#')[0] || workspaceId;
  }
  if (!projectId && pk.includes('PROJECT#')) {
    projectId = pk.split('PROJECT#')[1]?.split('#')[0] || projectId;
  }
  // Parent input/output ids: raw fields first, then the element GSI3SK
  // (INPUT#iid#ELEMENT#eid for input elements,
  //  INPUT#iidOUTPUT#oid#ELEMENT#eid for output elements), then PK fallback.
  let inputId: string | undefined = raw.inputId;
  let outputId: string | undefined = raw.outputId;
  if (!inputId || !outputId) {
    const gsi3 = String(raw.GSI3SK || '');
    let m = gsi3.match(/^INPUT#([^#]+)OUTPUT#([^#]+)#ELEMENT#/);
    if (m) {
      inputId = inputId || m[1];
      outputId = outputId || m[2];
    } else {
      m = gsi3.match(/^INPUT#([^#]+)#ELEMENT#/);
      if (m) inputId = inputId || m[1];
    }
  }
  if (!inputId && pk.startsWith('INPUT#')) inputId = pk.split('#')[1];
  if (!outputId && pk.startsWith('OUTPUT#')) outputId = pk.split('#')[1];
  return {
    id: elementId,
    PK: pk,
    SK: sk,
    elementId,
    fileName,
    name: raw.name || fileName,
    fileUrl: raw.fileUrl || raw.downloadUrl || raw.url || raw.thumbnail || '',
    size: Number(raw.size || 0),
    duration: raw.duration ? Number(raw.duration) : undefined,
    contentType: raw.contentType || raw.filetype || '',
    filetype: raw.filetype || raw.contentType || '',
    type: raw.type || 'file',
    language: raw.language || '',
    preview: raw.preview || raw.thumbnail || '',
    createdAt: raw.createdAt || raw.updatedAt || new Date().toISOString(),
    updatedAt: raw.updatedAt,
    projectId: projectId || raw.projectId,
    // Never fabricate "Project abc123" placeholders — names resolve via
    // ensureNames() from the real workspace/project lists; unknown stays
    // undefined so the UI hides the badge instead of showing sliced ids.
    projectName: raw.projectName || undefined,
    workspaceId: workspaceId || raw.workspaceId,
    workspaceName: raw.workspaceName || undefined,
    inputId,
    outputId,
    inputName: undefined,
    userId: raw.userId,
    status: raw.status === 'expired' ? 'expired' : (raw.status || 'available'),
    displayType: detectDisplayType(raw),
  };
}

/**
 * DEL04/DEL-R10 contract: the asset DELETE endpoint returns either
 * `{ deleted: true }` or HTTP 409 `{ deleted: false,
 * code: 'TRANSCRIPT_OPERATION_REQUIRED', transcriptId(s), managementPath }`.
 */
export const TRANSCRIPT_OPERATION_REQUIRED = 'TRANSCRIPT_OPERATION_REQUIRED';

export interface TranscriptOperationRejection {
  transcriptIds: string[];
  managementPath: string;
  message?: string;
}

/**
 * Extract a transcript-operation rejection from a response payload, if
 * present. The `code` is authoritative: both genuine HTTP 409s and
 * 200-with-code payloads are accepted. `status` is accepted for future
 * strictness but intentionally ignored today.
 */
export function readTranscriptOperationRequired(data: any, _status?: unknown): TranscriptOperationRejection | null {
  if (!data || typeof data !== 'object') return null;
  if ((data as any).code !== TRANSCRIPT_OPERATION_REQUIRED) return null;
  const ids: string[] = Array.isArray((data as any).transcriptIds)
    ? (data as any).transcriptIds.map(String)
    : typeof (data as any).transcriptId === 'string'
      ? [String((data as any).transcriptId)]
      : [];
  const managementPath =
    typeof (data as any).managementPath === 'string' && (data as any).managementPath
      ? String((data as any).managementPath)
      : '/Users/transcripts';
  return {
    transcriptIds: ids,
    managementPath,
    message: typeof (data as any).message === 'string' ? String((data as any).message) : undefined,
  };
}

/** Enriched deletion error carrying the Saved-transcripts management path. */
export class AssetTranscriptRequiredError extends Error {
  readonly transcriptIds: string[];
  readonly managementPath: string;
  readonly fileName: string;
  constructor(fileName: string, rejection: TranscriptOperationRejection) {
    super(
      rejection.message ||
      `This file has saved transcripts. Manage them in Saved transcripts before deleting. The file was kept.`,
    );
    this.name = 'AssetTranscriptRequiredError';
    this.fileName = fileName;
    this.transcriptIds = rejection.transcriptIds;
    this.managementPath = rejection.managementPath;
  }
}

export function assetTranscriptRequiredError(item: AssetItem, rejection: TranscriptOperationRejection) {
  return new AssetTranscriptRequiredError(item.fileName, rejection);
}

export function mockAssets(): AssetItem[] {
  // Kept for unit tests / manual seeding — fetchAssets no longer falls back
  // to it (silent mocks hid real listing regressions).
  const now = Date.now();
  const mk = (over: Partial<AssetItem> & { fileName: string; type: string; size?: number; duration?: number; createdAt: string }): AssetItem => {
    const base: any = {
      id: over.fileName,
      PK: `INPUT#mock-${over.fileName}`,
      SK: `ELEMENT#mock-${over.fileName}`,
      elementId: `mock-${over.fileName}`,
      fileName: over.fileName,
      name: over.fileName,
      fileUrl: '',
      size: over.size || 0,
      duration: over.duration,
      contentType: over.type,
      filetype: over.type,
      type: over.type.includes('audio') ? 'audio' : over.type.includes('video') ? 'video' : over.type.includes('image') ? 'image' : 'file',
      createdAt: over.createdAt,
      projectId: 'proj-mock-1',
      projectName: 'Demo Project',
      workspaceId: 'ws-mock-1',
      workspaceName: 'Demo Workspace',
      status: 'available',
      displayType: detectDisplayType({ fileName: over.fileName, contentType: over.type, type: over.type }),
    };
    return { ...base, ...over, displayType: detectDisplayType({ fileName: over.fileName, contentType: over.type, type: over.type }) } as AssetItem;
  };
  return [
    mk({ fileName: 'interview_2024-08-20.mp3', type: 'audio/mpeg', size: 48234412, duration: 1842, createdAt: new Date(now - 1000 * 60 * 60 * 2).toISOString() }),
    mk({ fileName: 'product_demo.mp4', type: 'video/mp4', size: 312445678, duration: 342, createdAt: new Date(now - 1000 * 60 * 60 * 24).toISOString() }),
    mk({ fileName: 'podcast_ep12.wav', type: 'audio/wav', size: 89234567, duration: 2450, createdAt: new Date(now - 1000 * 60 * 60 * 5).toISOString() }),
    mk({ fileName: 'cover_art.jpg', type: 'image/jpeg', size: 2456789, createdAt: new Date(now - 1000 * 60 * 60 * 48).toISOString() }),
    mk({ fileName: 'transcript_final.srt', type: 'text/srt', size: 45234, createdAt: new Date(now - 1000 * 60 * 30).toISOString() }),
    mk({ fileName: 'meeting_notes_2024-08-19.srt', type: 'text/srt', size: 12345, createdAt: new Date(now - 1000 * 60 * 60 * 72).toISOString() }),
    mk({ fileName: 'lecture_recording.m4a', type: 'audio/m4a', size: 67345234, duration: 3600, createdAt: new Date(now - 1000 * 60 * 60 * 96).toISOString() }),
    mk({ fileName: 'promo_clip.mov', type: 'video/quicktime', size: 523452345, duration: 45, createdAt: new Date(now - 1000 * 60 * 60 * 10).toISOString() }),
    mk({ fileName: 'thumbnail.png', type: 'image/png', size: 1234567, createdAt: new Date(now - 1000 * 60 * 60 * 120).toISOString() }),
    mk({ fileName: 'webinar_2024-08-18.mp3', type: 'audio/mpeg', size: 102345678, duration: 5400, createdAt: new Date(now - 1000 * 60 * 60 * 150).toISOString() }),
    mk({ fileName: 'subtitles_en.vtt', type: 'text/vtt', size: 23456, createdAt: new Date(now - 1000 * 60 * 60 * 3).toISOString() }),
    mk({ fileName: 'behind_scenes.mp4', type: 'video/mp4', size: 234567890, duration: 890, createdAt: new Date(now - 1000 * 60 * 60 * 200).toISOString() }),
  ];
}

export const useAssetsStore = defineStore('assetsStore', {
  state: (): AssetsState => ({
    items: [],
    filteredItems: [],
    loading: false,
    searching: false,
    error: null,
    searchQuery: '',
    fileTypeFilter: 'all',
    sortBy: 'newest',
    viewMode: 'grid',
    nextToken: null,
    hasMore: false,
    stats: { totalFiles: 0, totalSize: 0, byType: {}, byTypeSize: {} },
    previewItem: null,
    showPreview: false,
    showExpired: false,
    projectNames: {},
    workspaceNames: {},
    inputNames: {},
    outputInputs: {},
    inputNameReady: {},
    openingFileName: null,
  }),

  getters: {
    displayedItems(state): AssetItem[] {
      const list = [...state.filteredItems];
      // sort
      switch (state.sortBy) {
        case 'newest':
          list.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
          break;
        case 'oldest':
          list.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
          break;
        case 'name':
          list.sort((a, b) => a.fileName.localeCompare(b.fileName));
          break;
        case 'size':
          list.sort((a, b) => (b.size || 0) - (a.size || 0));
          break;
        case 'duration':
          list.sort((a, b) => (b.duration || 0) - (a.duration || 0));
          break;
      }
      return list;
    },
    isEmpty(state): boolean {
      return !state.loading && state.filteredItems.length === 0;
    },
  },

  actions: {
    applyFilters() {
      let list = [...this.items];
      if (this.searchQuery.trim().length > 0) {
        const q = this.searchQuery.trim().toLowerCase();
        list = list.filter((it) =>
          it.fileName.toLowerCase().includes(q) ||
          (it.projectName || '').toLowerCase().includes(q) ||
          (it.workspaceName || '').toLowerCase().includes(q) ||
          (it.preview || '').toLowerCase().includes(q)
        );
      }
      if (this.fileTypeFilter !== 'all') {
        list = list.filter((it) => {
          if (this.fileTypeFilter === 'srt') return it.displayType === 'srt' || it.fileName.toLowerCase().endsWith('.srt') || it.fileName.toLowerCase().endsWith('.vtt');
          if (this.fileTypeFilter === 'transcript') return it.displayType === 'transcript' || it.displayType === 'srt';
          return it.displayType === this.fileTypeFilter;
        });
      }
      this.filteredItems = list;
      this.computeStats();
    },

    computeStats() {
      const totalFiles = this.filteredItems.length;
      const totalSize = this.filteredItems.reduce((acc, it) => acc + (it.size || 0), 0);
      const byType: Record<string, number> = {};
      const byTypeSize: Record<string, number> = {};
      for (const it of this.filteredItems) {
        const k = it.displayType;
        byType[k] = (byType[k] || 0) + 1;
        byTypeSize[k] = (byTypeSize[k] || 0) + (it.size || 0);
      }
      this.stats = { totalFiles, totalSize, byType, byTypeSize };
    },

    setSearchQuery(q: string) {
      this.searchQuery = q;
      this.applyFilters();
    },

    setFileTypeFilter(t: AssetFileType) {
      this.fileTypeFilter = t;
      this.applyFilters();
    },

    setSortBy(s: AssetSortBy) {
      this.sortBy = s;
    },

    setViewMode(m: 'grid' | 'list') {
      this.viewMode = m;
      try { localStorage.setItem('assets-view-mode', m); } catch (e) { void e; }
    },

    setShowExpired(v: boolean) {
      this.showExpired = v;
      this.fetchAssets({ reset: true });
    },

    // Resolve real workspace/project display names for the loaded items.
    // Runs after fetch; patches items in place and re-applies filters so
    // project-name search works on real names, not fabricated ids.
    async ensureNames() {
      try {
        const { useDashboardStore } = await import('@stores/dashboardStore');
        const dashboardStore = useDashboardStore();
        if (dashboardStore.workspaces.length === 0) {
          await dashboardStore.refreshWorkspaces({ silent: true }).catch(() => undefined);
        }
        for (const ws of dashboardStore.workspaces) {
          const wsId = ws.PK?.split('#')[1];
          if (wsId && !this.workspaceNames[wsId]) this.workspaceNames[wsId] = ws.name || wsId;
        }
        // Items whose rows lack workspaceId (element rows predate it) only
        // carry projectId: find the owning workspace first so the project
        // list below queries a real workspace instead of nothing.
        const orphanProjects = [...new Set(
          this.items.filter((i) => i.projectId && !i.workspaceId).map((i) => i.projectId as string)
        )];
        if (orphanProjects.length > 0) {
          const allWs = dashboardStore.workspaces.map((w) => w.PK?.split('#')[1]).filter((w): w is string => !!w);
          for (const wsId of allWs.slice(0, 10)) {
            if (orphanProjects.every((p) => this.items.some((i) => i.projectId === p && i.workspaceId))) break;
            try {
              const resp = await AutoService.fetchOrder(`workspaces/${wsId}/projects`);
              const list = Array.isArray(resp?.Items) ? resp.Items : [];
              const ids = new Set(list.map((pr: any) => String(pr.SK || '').split('#')[1]));
              for (const it of this.items) {
                if (it.projectId && !it.workspaceId && ids.has(it.projectId)) it.workspaceId = wsId;
              }
              for (const pr of list) {
                const pid = String(pr.SK || '').split('#')[1];
                if (pid && !this.projectNames[pid]) this.projectNames[pid] = pr.name || pid;
              }
            } catch (e) { void e; }
          }
        }
        const unknownProjects = [...new Set(
          this.items.map((i) => i.projectId).filter((p): p is string => !!p && !this.projectNames[p])
        )];
        if (unknownProjects.length > 0) {
          const wsIds = [...new Set(
            this.items.map((i) => i.workspaceId).filter((w): w is string => !!w)
          )];
          const scopes = wsIds.length > 0 ? wsIds : dashboardStore.workspaces.map((w) => w.PK?.split('#')[1]).filter((w): w is string => !!w);
          for (const wsId of scopes.slice(0, 10)) {
            if (unknownProjects.every((p) => this.projectNames[p])) break;
            try {
              const resp = await AutoService.fetchOrder(`workspaces/${wsId}/projects`);
              const list = Array.isArray(resp?.Items) ? resp.Items : [];
              for (const p of list) {
                const pid = String(p.SK || '').split('#')[1];
                if (pid && !this.projectNames[pid]) this.projectNames[pid] = p.name || pid;
              }
            } catch (e) { void e; }
          }
        }
        let changed = false;
        for (const it of this.items) {
          if (it.projectId && this.projectNames[it.projectId] && it.projectName !== this.projectNames[it.projectId]) {
            it.projectName = this.projectNames[it.projectId];
            changed = true;
          }
          if (it.workspaceId && this.workspaceNames[it.workspaceId] && it.workspaceName !== this.workspaceNames[it.workspaceId]) {
            it.workspaceName = this.workspaceNames[it.workspaceId];
            changed = true;
          }
        }
        if (changed) this.applyFilters();
      } catch (e) { void e; }
    },

    // Input display name for an asset (resolved via ensureInputNames).
    inputNameFor(item: AssetItem): string {
      const iid = item.inputId || (item.outputId ? this.outputInputs[item.outputId] : undefined);
      if (iid && this.inputNames[iid]) return this.inputNames[iid];
      return item.inputName || '';
    },

    // Resolve parent input names per project via the project's `complete`
    // payload (inputs carry names; outputs map outputId back to inputId).
    // Best-effort background enrichment, same pattern as ensureNames().
    async ensureInputNames() {
      try {
        const need = this.items.filter(
          (it) => !this.inputNameFor(it) && (it.inputId || it.outputId) && it.projectId && it.workspaceId
        );
        if (need.length === 0) return;
        const groups = new Map<string, { ws: string; proj: string }>();
        for (const it of need) {
          const key = `${it.workspaceId}#${it.projectId}`;
          if (!this.inputNameReady[key] && !groups.has(key) && groups.size < 10) {
            groups.set(key, { ws: it.workspaceId as string, proj: it.projectId as string });
          }
        }
        for (const [key, scope] of groups) {
          try {
            const resp = await AutoService.fetchOrder(
              `workspaces/${scope.ws}/projects/${scope.proj}/complete`
            );
            const mixed = Array.isArray(resp?.Items) ? resp.Items : [];
            for (const row of mixed) {
              const pk = String(row.PK || '');
              const sk = String(row.SK || '');
              if (pk.startsWith('PROJECT#') && sk.startsWith('INPUT#')) {
                const iid = sk.split('#')[1];
                if (iid && !this.inputNames[iid]) this.inputNames[iid] = row.name || row.fileName || iid;
              } else if (pk.startsWith('INPUT#') && sk.startsWith('OUTPUT#')) {
                const iid = pk.split('#')[1];
                const oid = sk.split('#')[1];
                if (oid && iid && !this.outputInputs[oid]) this.outputInputs[oid] = iid;
              }
            }
            this.inputNameReady[key] = true;
          } catch (e) { void e; }
        }
        let changed = false;
        for (const it of this.items) {
          const nm = this.inputNameFor(it);
          if (nm && it.inputName !== nm) {
            it.inputName = nm;
            changed = true;
          }
        }
        if (changed) this.applyFilters();
      } catch (e) { void e; }
    },

    // Resolve owning project/workspace for an asset that carries no context
    // (deep-link fallback) via GET /api/assets?elementId=.
    async resolveContext(elementId: string): Promise<{ projectId?: string; workspaceId?: string } | null> {
      try {
        const domain = useSessionStore().appConfig?.domain || '';
        if (!domain || !elementId) return null;
        const res = await axios.get(`${domain}api/assets`, { params: { elementId } });
        const d = res.data || {};
        if (!d.projectId) return null;
        return { projectId: d.projectId, workspaceId: d.workspaceId };
      } catch (e) { void e; return null; }
    },

    async fetchAssets(opts?: { reset?: boolean; nextToken?: string | null }) {
      const reset = opts?.reset ?? true;
      if (reset) {
        this.loading = true;
        this.error = null;
        this.nextToken = null;
        this.hasMore = false;
      } else {
        this.searching = true;
      }

      try {
        const domain = useSessionStore().appConfig?.domain || '';
        if (!domain) throw new Error('no domain');
        let fetched: any[] = [];
        let nextTok: string | null = null;

        // Route to the endpoint built for the job: /api/assets/search filters
        // server-side across the whole library (the plain list ignores `q`,
        // so sending q there only filtered the loaded page and hid matches).
        // No mock fallback: an empty list is a real empty list — silent mock
        // data is exactly what hid "recent uploads don't show" regressions.
        const trimmed = this.searchQuery.trim();
        const params: any = {};
        if (this.fileTypeFilter !== 'all') params.fileType = this.fileTypeFilter;
        params.sortBy = 'createdAt';
        params.sortOrder = 'desc';
        if (this.showExpired) params.includeExpired = 'true';
        if (opts?.nextToken) params.nextToken = opts.nextToken;
        const endpoint = trimmed.length >= 2
          ? `${domain}api/assets/search`
          : `${domain}api/assets`;
        if (trimmed.length >= 2) params.q = trimmed;
        const res = await axios.get(endpoint, { params });
        const data = res.data;
        if (data) {
          // Normalize various response shapes (backend sends {items:[]})
          if (Array.isArray(data)) {
            fetched = data;
          } else if (Array.isArray(data.Items)) {
            fetched = data.Items;
            nextTok = data.LastEvaluatedKey ? JSON.stringify(data.LastEvaluatedKey) : data.nextToken || null;
          } else if (Array.isArray(data.items)) {
            fetched = data.items;
            nextTok = data.nextToken || data.LastEvaluatedKey || null;
          } else if (Array.isArray(data.assets)) {
            fetched = data.assets;
            nextTok = data.nextToken || null;
          } else if (Array.isArray(data.data)) {
            fetched = data.data;
            nextTok = data.nextToken || null;
          } else if (data.Item) {
            fetched = [data.Item];
          } else if (data.elementId || data.fileName) {
            fetched = [data];
          } else {
            // unknown shape, try to extract
            fetched = [];
          }
        }
        const normalized = fetched.map(normalizeAsset);
        if (reset) {
          this.items = normalized;
        } else {
          // dedupe by id
          const existingIds = new Set(this.items.map((i) => i.id));
          const newOnes = normalized.filter((n) => !existingIds.has(n.id));
          this.items = [...this.items, ...newOnes];
        }
        this.nextToken = nextTok;
        this.hasMore = !!nextTok;

        this.applyFilters();
        // Resolve real workspace/project names in the background (patches
        // items + re-filters so project-name search matches real names).
        void this.ensureNames();
        // Same for parent input names shown on the cards.
        void this.ensureInputNames();
      } catch (e: any) {
        this.error = e?.message || 'Failed to load assets';
        console.error('[assetsStore] fetchAssets error', e);
      } finally {
        this.loading = false;
        this.searching = false;
      }
    },

    async searchAssets(query: string) {
      this.searchQuery = query;
      // client-side filter is instant; also trigger server search debounced by caller
      this.applyFilters();
      // if query length >=2, try server search
      if (query.trim().length >= 2) {
        await this.fetchAssets({ reset: true });
      } else if (query.trim().length === 0) {
        await this.fetchAssets({ reset: true });
      }
    },

    async loadMore() {
      if (!this.hasMore || !this.nextToken) return;
      await this.fetchAssets({ reset: false, nextToken: this.nextToken });
    },

    // DEL04/DEL-R10: the asset DELETE contract is `deleted: true` on success
    // or HTTP 409 `TRANSCRIPT_OPERATION_REQUIRED` with `transcriptIds` /
    // `managementPath` when the row carries saved transcripts. A rejected
    // delete keeps the item visible and throws an enriched error carrying
    // that management path so the UI can offer a direct action to
    // /Users/transcripts. Never report success for a failed delete.
    // Shows the global opening overlay (components/OpeningOverlay.vue); an
    // empty name shows the spinner alone. null = hidden.
    markOpening(fileName = '') {
      this.openingFileName = fileName;
    },
    clearOpening() {
      this.openingFileName = null;
    },
    async deleteAsset(item: AssetItem) {
      // The backend answers 503 "File deletion incomplete; retry" when its
      // storage settlement cannot prove a durable result yet. The operation
      // is idempotent (conditional receipt fence + idempotent S3 delete), so
      // a single delayed retry is safe and matches the backend contract.
      let retried = false;
      for (;;) {
        try {
          const domain = useSessionStore().appConfig?.domain || '';
          if (!domain) throw new Error('API configuration unavailable');
          const response = await axios.delete(`${domain}api/assets/${encodeURIComponent(item.elementId)}`);
          const rejection = readTranscriptOperationRequired(response?.data, response?.status);
          if (rejection) {
            const err = assetTranscriptRequiredError(item, rejection);
            notify('danger', err.message);
            throw err;
          }
          if (response.data?.deleted !== true) throw new Error(response.data?.message || 'Deletion was not completed');
          this.items = this.items.filter((i) => i.id !== item.id);
          this.applyFilters();
          notify('success', `Deleted ${item.fileName}`);
          return true;
        } catch (e: any) {
          // Axios surfaces HTTP 409 as a thrown error: extract the backend's
          // transcript-management payload before falling back to a generic
          // failure. The failed item stays visible in both branches.
          const rejection = readTranscriptOperationRequired(e?.response?.data, e?.response?.status);
          if (rejection && !(e instanceof AssetTranscriptRequiredError)) {
            const err = assetTranscriptRequiredError(item, rejection);
            notify('danger', err.message);
            throw err;
          }
          if (e instanceof AssetTranscriptRequiredError) throw e;
          if (e?.response?.status === 503 && !retried) {
            retried = true;
            notify('warning', e?.response?.data?.message || 'Deletion incomplete — retrying once');
            await new Promise((r) => setTimeout(r, 1500));
            continue;
          }
          notify('danger', e?.response?.data?.message || e?.message || 'Failed to delete asset');
          throw e;
        }
      }
    },

    // DEL04: downloads use the signed URL already attached to the listed
    // item (`fileUrl` / `downloadUrl` from GET /api/assets). There is no
    // GET /api/assets/{id}/download route in rest-api-stack, so no guessed
    // fallback URL is attempted. A missing URL is an explicit recoverable
    // "unavailable — refresh and retry" error, never fabricated content.
    async downloadAsset(item: AssetItem) {
      try {
        if (item.status === 'expired') {
          notify('warning', `No longer available: ${item.fileName} (file expired and was auto-deleted)`);
          return;
        }
        // Signed URL attached at list time (assets handler signs downloadUrl;
        // normalizeAsset maps it onto fileUrl). Refresh the list to mint a
        // fresh URL when this one expired; there is no per-asset download
        // route to fall back to.
        const url = item.fileUrl || (item as unknown as { downloadUrl?: string }).downloadUrl || '';
        if (url) {
          if (url.startsWith('http')) {
            window.open(url, '_blank');
          } else {
            // data url or relative
            const a = document.createElement('a');
            a.href = url;
            a.download = item.fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
          }
          notify('success', `Downloading ${item.fileName}`);
        } else {
          // No signed URL on the item (never uploaded, already removed, or
          // the hourly signed link was not minted for this row). Refreshing
          // the list mints fresh links; retry after refresh.
          notify('warning', `No download available for ${item.fileName} — the download link is missing or expired. Refresh the list and retry.`);
        }
      } catch (e: any) {
        notify('danger', e?.message || 'Download failed');
        throw e;
      }
    },

    openPreview(item: AssetItem) {
      this.previewItem = item;
      this.showPreview = true;
    },

    closePreview() {
      this.showPreview = false;
      this.previewItem = null;
    },

    initFromStorage() {
      try {
        const vm = localStorage.getItem('assets-view-mode');
        if (vm === 'grid' || vm === 'list') this.viewMode = vm;
      } catch (e) { void e; }
    },
  },
});
